import { useCallback, useState } from 'react';
import { View } from 'react-native';
import { Colors, Flex, Typography } from '@banhala/ui-kit';
import type { HeadlineXsmallOneLineSegmentsProps, SegmentItem } from './types';
import { isNil } from '../../../../utils';
import { useLogAnalyticsEvent } from '../../../../hooks/useLogAnalyticsEvent';
import { useFilterListAtom } from '../../ItemList/FilterList/Provider/filterList/useFilterListAtom';

export const HeadlineXsmallOneLineSegments = ({
  header,
  scrollToFilterList,
}: HeadlineXsmallOneLineSegmentsProps) => {
  const { setFilterListAtom } = useFilterListAtom();
  const logAnalyticsEvent = useLogAnalyticsEvent();
  const [selectedValue, setSelectedValue] = useState(
    () => header?.item.segments.find(s => s.is_selected)?.query_value ?? '',
  );

  const handleSelectSegment = useCallback(
    (segment: SegmentItem) => {
      if (isNil(header)) return;

      const eventName = header.item.analytics_type
        ? `CLICK_${header.item.analytics_type}`
        : 'CLICK_SEGMENT';

      logAnalyticsEvent({
        name: eventName,
        analytics: { ...header.logging.analytics, ...segment.logging?.analytics },
        ably_only_log: { ...header.logging.ably_only_log, ...segment.logging?.ably_only_log },
      });

      setSelectedValue(segment.query_value);
      scrollToFilterList?.();
      setFilterListAtom(prev => ({
        ...prev,
        [segment.query_name]: [segment.query_value],
      }));
    },
    [header, scrollToFilterList, setFilterListAtom, logAnalyticsEvent],
  );

  if (isNil(header)) return null;

  return (
    <Flex.Row
      padding={{ top: 16, bottom: 12, horizontal: 16 }}
      alignItems="center"
    >
      <Typography type="subtitle1" color="content_primary">
        {header.item.title}
      </Typography>
      <Flex.Row margin={{ left: 12 }} alignItems="center">
        {header.item.segments.map((segment, index) => {
          const isSelected = segment.query_value === selectedValue;
          return (
            <Flex.Row key={segment.query_value} alignItems="center">
              {index > 0 && (
                <View
                  style={{
                    width: 1,
                    height: 8,
                    backgroundColor: Colors.border_tertiary,
                    marginHorizontal: 8,
                  }}
                />
              )}
              <Flex onPress={() => handleSelectSegment(segment)}>
                <Typography
                  type={isSelected ? 'subtitle3' : 'body3'}
                  color={isSelected ? 'content_primary' : 'content_tertiary'}
                >
                  {segment.title}
                </Typography>
              </Flex>
            </Flex.Row>
          );
        })}
      </Flex.Row>
    </Flex.Row>
  );
};
